import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { getAgeBand, isAreaAllowed } from '../utils/ageBands';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiCheck, FiStar, FiInfo, FiHeart, FiSmile, FiCpu } = FiIcons;

const QUEST_AREAS = [
  {
    id: 'starter-jungle',
    name: 'Starter Jungle',
    description: 'Begin your learning adventure!',
    background: 'from-green-400 to-green-600',
    icon: '🌱',
    requiredLevel: 1,
    quests: [
      { id: 'counting', name: 'Counting Safari', icon: '🦁', difficulty: 1, subject: 'Math', tokens: 10 },
      { id: 'colors', name: 'Color Explorer', icon: '🌈', difficulty: 1, subject: 'Science', tokens: 10 },
      { id: 'shapes', name: 'Shape Adventure', icon: '🔷', difficulty: 1, subject: 'Math', tokens: 10 },
      { id: 'cleanup-quest', name: 'Cleanup Quest', icon: '🧹', difficulty: 1, subject: 'Life Skills', tokens: 15 },
      { id: 'reading-challenge', name: 'Reading Quest', icon: '📖', difficulty: 1, subject: 'Language', tokens: 50 },
    ],
  },
  {
    id: 'feelings-valley',
    name: 'Feelings Valley',
    description: 'SEL for Ages 4-6: Learn emotions and kindness!',
    background: 'from-rose-400 to-pink-500',
    icon: '❤️',
    requiredLevel: 1,
    minAge: 4,
    maxAge: 6,
    quests: [
      { id: 'sel-teddy-playground', name: "Teddy's Playground", icon: '🧸', difficulty: 1, subject: 'SEL', tokens: 15, desc: 'Right or Wrong scenarios' },
      { id: 'sel-feelings-faces', name: 'Feelings Faces', icon: '😊', difficulty: 1, subject: 'SEL', tokens: 15, desc: 'Emotion recognition' },
      { id: 'sel-bunny-choices', name: "Bunny's Choices", icon: '🐰', difficulty: 1, subject: 'SEL', tokens: 15, desc: 'Branching stories' },
      { id: 'sel-calm-bubbles', name: 'Calm-Down Bubbles', icon: '🫧', difficulty: 1, subject: 'SEL', tokens: 15, desc: 'Self-regulation' },
    ],
  },
  {
    id: 'empathy-gardens',
    name: 'Empathy Gardens',
    description: 'SEL for Ages 6-8: Kindness and coping skills!',
    background: 'from-purple-400 to-violet-500',
    icon: '🌸',
    requiredLevel: 1,
    minAge: 6,
    maxAge: 8,
    quests: [
      { id: 'sel-zara-leo', name: 'Zara & Leo', icon: '🎒', difficulty: 2, subject: 'SEL', tokens: 20, desc: 'Classroom adventures' },
      { id: 'sel-kindness-post', name: 'Kindness Post Office', icon: '💌', difficulty: 2, subject: 'SEL', tokens: 20, desc: 'Prosocial behavior' },
      { id: 'sel-neighborhood', name: 'Neighborhood Dilemmas', icon: '🏘️', difficulty: 2, subject: 'SEL', tokens: 20, desc: 'Moral reasoning' },
      { id: 'sel-emotion-backpack', name: 'Emotion Backpack', icon: '🎒', difficulty: 2, subject: 'SEL', tokens: 20, desc: 'Coping strategies' },
    ],
  },
  {
    id: 'thinking-temple',
    name: 'Thinking Temple',
    description: 'Challenge your mind with puzzles!',
    background: 'from-blue-400 to-blue-600',
    icon: '🏛️',
    requiredLevel: 1,
    minAge: 6,
    quests: [
      { id: 'math', name: 'Math Jungle', icon: '🧮', difficulty: 2, subject: 'Math', tokens: 15 },
      { id: 'number-jumps', name: 'Number Jumps', icon: '🐸', difficulty: 2, subject: 'Math', tokens: 20 },
      { id: 'patterns', name: 'Pattern Quest', icon: '🔄', difficulty: 2, subject: 'Logic', tokens: 15 },
      { id: 'science', name: 'Science Lab', icon: '🔬', difficulty: 2, subject: 'Science', tokens: 15 },
    ],
  },
  {
    id: 'perspective-peaks',
    name: 'Perspective Peaks',
    description: 'SEL for Ages 8-10: Empathy and conflict resolution!',
    background: 'from-indigo-400 to-purple-600',
    icon: '🏔️',
    requiredLevel: 2,
    minAge: 8,
    maxAge: 10,
    quests: [
      { id: 'sel-digital-diaries', name: 'Digital World Diaries', icon: '💻', difficulty: 2, subject: 'SEL', tokens: 25, desc: 'Digital citizenship' },
      { id: 'sel-empathy-swap', name: 'Empathy Swap Machine', icon: '🔄', difficulty: 3, subject: 'SEL', tokens: 25, desc: 'Perspective-taking' },
      { id: 'sel-conflict-court', name: 'Conflict Court', icon: '⚖️', difficulty: 3, subject: 'SEL', tokens: 25, desc: 'Conflict resolution' },
      { id: 'sel-friendship-bridge', name: 'Friendship Bridge', icon: '🌉', difficulty: 3, subject: 'SEL', tokens: 25, desc: 'Social skills' },
    ],
  },
  {
    id: 'multiplication-temple',
    name: 'Multiplication Temple',
    description: 'Master your times tables!',
    background: 'from-amber-400 via-orange-400 to-red-500',
    icon: '🕌',
    requiredLevel: 1,
    minAge: 7,
    quests: [
      { id: 'multiplication-treasure-hunt', name: 'Treasure Hunt', icon: '🏴‍☠️', difficulty: 2, subject: 'Math', tokens: 20 },
      { id: 'multiplication-jungle', name: 'Jungle Hunt', icon: '💎', difficulty: 2, subject: 'Math', tokens: 20 },
      { id: 'multiplication-island', name: 'Island Blitz', icon: '⚡', difficulty: 3, subject: 'Math', tokens: 25 },
      { id: 'multiplication-sudoku', name: 'Times Sudoku', icon: '🔢', difficulty: 3, subject: 'Math', tokens: 30 },
    ],
  },
  {
    id: 'resilience-fortress',
    name: 'Resilience Fortress',
    description: 'SEL for Ages 10-12: Peer pressure and ethics!',
    background: 'from-slate-400 to-gray-600',
    icon: '🏰',
    requiredLevel: 3,
    minAge: 10,
    maxAge: 12,
    quests: [
      { id: 'sel-peer-pressure', name: 'Peer Pressure Files', icon: '🕵️', difficulty: 3, subject: 'SEL', tokens: 30, desc: 'Resistance skills' },
      { id: 'sel-inner-voice', name: 'Inner Voice Coach', icon: '💭', difficulty: 3, subject: 'SEL', tokens: 30, desc: 'Growth mindset' },
      { id: 'sel-ethical-news', name: 'Ethical Newsroom', icon: '📰', difficulty: 3, subject: 'SEL', tokens: 30, desc: 'Ethical reasoning' },
      { id: 'sel-global-empathy', name: 'Global Empathy Atlas', icon: '🌍', difficulty: 3, subject: 'SEL', tokens: 30, desc: 'Global awareness' },
    ],
  },
  {
    id: 'wisdom-peaks',
    name: 'Wisdom Peaks',
    description: 'Master advanced challenges!',
    background: 'from-violet-400 to-purple-600',
    icon: '⛰️',
    requiredLevel: 2,
    minAge: 7,
    quests: [
      { id: 'memory', name: 'Memory Match', icon: '🧠', difficulty: 3, subject: 'Logic', tokens: 20 },
      { id: 'logic', name: 'Logic Puzzles', icon: '🧩', difficulty: 3, subject: 'Logic', tokens: 20 },
    ],
  },
];

function DifficultyStars({ difficulty }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3].map(i => (
        <span key={i} className={`text-xs ${i <= difficulty ? 'text-yellow-400' : 'text-gray-300'}`}>⭐</span>
      ))}
    </div>
  );
}

function QuestMap() {
  const navigate = useNavigate();
  const { state } = useGame();
  const age = state.age || 7;
  const band = state.ageBand || getAgeBand(age);

  const isAreaUnlocked = (area) => {
    const ageAllowed = isAreaAllowed(area.id, age);
    const levelOk = state.level >= area.requiredLevel;
    const minAgeOk = !area.minAge || age >= area.minAge;
    const maxAgeOk = !area.maxAge || age <= area.maxAge;
    return ageAllowed && levelOk && minAgeOk && maxAgeOk;
  };

  const isQuestCompleted = (questId) => state.completedQuests?.includes(questId);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="min-h-screen p-4 pt-24 pb-32">
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center mb-6">
          <h1 className="text-4xl font-kid text-jungle-700 mb-1">Quest Map 🗺️</h1>
          <p className="font-comic text-jungle-600">
            {band.emoji} {band.label} Explorer — Choose your adventure, {state.playerName}!
          </p>
        </motion.div>

        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { emoji: '🪙', value: state.tokens || 0, label: 'Tokens' },
            { emoji: '🏆', value: state.completedQuests?.length || 0, label: 'Completed' },
            { emoji: '🎯', value: state.level, label: 'Level' },
            { emoji: '🧠', value: `${state.gameStats?.totalAnswers > 0 ? Math.round((state.gameStats.correctAnswers / state.gameStats.totalAnswers) * 100) : 0}%`, label: 'Accuracy' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-3 shadow-lg border-2 border-safari-200 text-center">
              <div className="text-2xl mb-1">{s.emoji}</div>
              <div className="font-kid text-jungle-700 text-xl">{s.value}</div>
              <div className="font-comic text-jungle-600 text-xs">{s.label}</div>
            </div>
          ))}
        </motion.div>

        <div className="space-y-10">
          {QUEST_AREAS.map((area, areaIndex) => {
            const unlocked = isAreaUnlocked(area);
            const ageRecommended = !area.minAge || age >= area.minAge;
            const isSEL = area.id.includes('feelings') || area.id.includes('empathy') || area.id.includes('perspective') || area.id.includes('resilience');
            
            return (
              <motion.div key={area.id}
                initial={{ x: areaIndex % 2 === 0 ? -80 : 80, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 + areaIndex * 0.1 }}
                className={!unlocked ? 'opacity-50 grayscale' : ''}>
                <div className={`bg-gradient-to-r ${area.background} rounded-3xl p-5 mb-5 text-white shadow-lg relative overflow-hidden`}>
                  {isSEL && (
                    <div className="absolute top-2 right-2 bg-white/20 px-3 py-1 rounded-full text-xs font-comic">
                      <SafeIcon icon={FiHeart} className="inline mr-1" /> SEL Zone
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl bg-white/20 p-3 rounded-2xl">{area.icon}</div>
                      <div>
                        <h2 className="text-2xl font-kid">{area.name}</h2>
                        <p className="font-comic opacity-90 text-sm">{area.description}</p>
                      </div>
                    </div>
                    {!unlocked ? (
                      <div className="flex items-center gap-2 bg-black/30 px-3 py-2 rounded-xl text-sm font-kid border border-white/20">
                        <SafeIcon icon={FiLock} />
                        {area.minAge ? `Age ${area.minAge}+ Required` : `Level ${area.requiredLevel} Required`}
                      </div>
                    ) : !ageRecommended ? (
                      <div className="flex items-center gap-2 bg-blue-500/30 px-3 py-2 rounded-xl text-sm font-comic border border-white/20">
                        <SafeIcon icon={FiInfo} /> Recommended Age {area.minAge}+
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {area.quests.map((quest, qi) => {
                    const completed = isQuestCompleted(quest.id);
                    return (
                      <motion.button key={`${quest.id}-${qi}`}
                        whileHover={unlocked ? { scale: 1.04, y: -6 } : {}}
                        whileTap={unlocked ? { scale: 0.96 } : {}}
                        onClick={() => unlocked && navigate(`/game/${quest.id}`)}
                        disabled={!unlocked}
                        className={`bg-white rounded-[2rem] p-5 shadow-xl border-4 text-left relative overflow-hidden flex flex-col h-full transition-all duration-300 ${unlocked ? 'border-safari-100 hover:border-safari-300 cursor-pointer' : 'border-gray-100 cursor-not-allowed'}`}>
                        {completed && (
                          <div className="absolute top-3 right-3 bg-green-500 text-white rounded-full p-1.5 shadow z-10">
                            <SafeIcon icon={FiCheck} className="text-sm" />
                          </div>
                        )}
                        <div className="flex flex-col flex-1">
                          <div className="text-4xl mb-3 bg-jungle-50 w-14 h-14 flex items-center justify-center rounded-xl self-center">{quest.icon}</div>
                          <div className="text-center mb-3">
                            <h3 className="font-kid text-lg text-jungle-800 mb-1 leading-tight">{quest.name}</h3>
                            {quest.desc && (
                              <p className="font-comic text-xs text-jungle-500">{quest.desc}</p>
                            )}
                            <div className="flex items-center justify-center gap-2 mt-1">
                              <span className="font-comic text-xs text-jungle-500 uppercase tracking-wide">{quest.subject}</span>
                              <DifficultyStars difficulty={quest.difficulty} />
                            </div>
                          </div>
                          <div className="mt-auto pt-3 border-t-2 border-dashed border-gray-100 flex justify-between items-center">
                            <div className="flex items-center gap-1">
                              <span className="text-yellow-500">🪙</span>
                              <span className="font-kid text-jungle-700 text-sm">+{quest.tokens}</span>
                            </div>
                            <span className={`font-comic text-xs font-bold px-2 py-0.5 rounded-full ${completed ? 'bg-green-100 text-green-700' : unlocked ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                              {completed ? 'DONE!' : unlocked ? 'PLAY' : 'LOCKED'}
                            </span>
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
          className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 bg-white px-5 py-3 rounded-full shadow border-2 border-safari-100 font-comic text-jungle-600 text-sm">
            <SafeIcon icon={FiStar} className="text-yellow-500" />
            Complete quests to earn tokens for the Avatar Shop!
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default QuestMap;